"use client";

import { RefreshCw } from "lucide-react";

import {
  RecommendedSlotSection,
  SchedulingVerificationControls,
} from "@/src/components/comms/referral-scheduling-recommendation";
import { SchedulingEligibility } from "@/src/components/comms/scheduling-eligibility";
import { Button } from "@/src/components/shadcn/ui/button";
import { ValerieCard } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { useVerifySchedulingRecommendation } from "@/src/lib/data-fetchers/scheduling-fetchers";
import {
  useRegenerateTaskSchedulingRecommendation,
  useTaskSchedulingRecommendation,
} from "@/src/lib/data-fetchers/tasks-fetchers";
import { Referral } from "@/src/models/documents/referral";
import { shouldRecommendForSchedulingInReferralTask } from "@/src/models/scheduling-recommendation";

export function TaskSchedulingRecommendation({
  taskId,
  referral,
  preferAgenticReceivingProvider = false,
}: {
  taskId: string;
  referral: Referral;
  preferAgenticReceivingProvider?: boolean;
}) {
  const { group } = useUser();
  const { recommendation, queryKey, isPending, isError } = useTaskSchedulingRecommendation(taskId);
  const regenerate = useRegenerateTaskSchedulingRecommendation(taskId);
  const verify = useVerifySchedulingRecommendation(referral.id, queryKey);
  const isMutating = regenerate.isPending || verify.isPending;

  return (
    <ValerieCard
      title="Scheduling recommendation"
      actions={
        <Button size="sm" variant="outline" disabled={isPending || isMutating} onClick={() => regenerate.mutate()}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${regenerate.isPending ? "animate-spin" : ""}`} />
          {regenerate.isPending ? "Regenerating..." : "Regenerate"}
        </Button>
      }
    >
      {isPending ? (
        <p className="text-sm text-muted-foreground">Checking for eligibility...</p>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Unable to load recommendation.</p>
      ) : recommendation ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            {shouldRecommendForSchedulingInReferralTask(group, recommendation) ? "Recommended" : "Not Recommended"}
            <SchedulingEligibility
              agenticRecommendation={recommendation.agenticRecommendation}
              receivingProvider={referral.receivingProvider}
              preferAgenticReceivingProvider={preferAgenticReceivingProvider}
            />
          </div>
          <RecommendedSlotSection recommendation={recommendation} />
          <SchedulingVerificationControls
            isCorrect={recommendation.isCorrect}
            verificationReason={recommendation.verificationReason}
            isPending={isMutating}
            onMarkCorrect={() =>
              verify.mutate({ isCorrect: recommendation.isCorrect === true ? null : true, verificationReason: null })
            }
            onMarkIncorrect={(reason) => verify.mutate({ isCorrect: false, verificationReason: reason })}
            onClear={() => verify.mutate({ isCorrect: null, verificationReason: null })}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No scheduling recommendation available.</p>
      )}
    </ValerieCard>
  );
}
